import { action, observable } from 'mobx'
import type { screenSearchData } from '../SearchStore/SearchStore'
import type { $RootStore } from '../../../src/stores/RootStore'

const STORAGE_SUFFIX = 'SEARCH_MANIFEST'
const REMOTE_FETCH_TIMEOUT_MS = 10000

export function parseSearchManifestResponse(data: unknown): screenSearchData[] | null {
  if (!data || typeof data !== 'object') return null
  const terms = (data as { terms?: unknown }).terms
  if (!Array.isArray(terms) || terms.length === 0) return null
  return terms as screenSearchData[]
}

export function parseStoredSearchManifestCache(data: unknown): { apiVersion: string | null, terms: screenSearchData[] } {
  if (!data || typeof data !== 'object') return { apiVersion: null, terms: [] }
  const { apiVersion, terms } = data as { apiVersion: string | null, terms: screenSearchData[] }
  return { apiVersion, terms: Array.isArray(terms) ? terms : [] }
}

export function getSearchManifestUrlFromConfig(config: Record<string, unknown>): string | undefined {
  const pathOrUrl = config.searchManifestUrl
  const apiDefault = config.apiDefault
  if (!apiDefault || typeof apiDefault !== 'string') return undefined
  if (!pathOrUrl || typeof pathOrUrl !== 'string') return undefined
  return `http${config.name === 'development' ? '' : 's'}://${config.apiDefault}${pathOrUrl}`
}

export default class SearchManifestStore {
  rootStore: $RootStore

  @observable accessor activeTerms: screenSearchData[] = []

  private lastPersistedApiVersion: string | null = null

  private backgroundRefreshStarted = false

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore
  }

  private get storageKey(): string {
    return `${this.rootStore.storeDataSync.keyPrefix}${STORAGE_SUFFIX}`
  }

  private get remoteUrl(): string | undefined {
    return getSearchManifestUrlFromConfig(this.rootStore.config)
  }

  @action.bound setActiveTerms(terms: screenSearchData[]) {
    this.activeTerms = terms
  }

  hydrateFromStorage = async (): Promise<void> => {
    const { storage } = this.rootStore.storeDataSync
    if (!storage) return
    try {
      const raw = await storage.getItem(this.storageKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as unknown
      const { apiVersion, terms } = parseStoredSearchManifestCache(parsed)
      if (terms) {
        this.setActiveTerms(terms)
        this.lastPersistedApiVersion = apiVersion
      }
    } catch (error) {
      this.rootStore.errorHandler.captureException(error, {
        context: 'SearchManifestStore.hydrateFromStorage',
      })
    }
  }

  private persistToStorage = async (
    terms: screenSearchData[],
    apiVersion: string | null,
  ): Promise<void> => {
    const { storage } = this.rootStore.storeDataSync
    if (!storage) return
    try {
      await storage.setItem(this.storageKey, JSON.stringify({ apiVersion, terms }))
      this.lastPersistedApiVersion = apiVersion
    } catch (error) {
      this.rootStore.errorHandler.captureException(error, {
        context: 'SearchManifestStore.persistToStorage',
      })
    }
  }

  /** Fetch remote manifest with timeout; keeps bundled/cache terms on failure. */
  fetchRemote = async (): Promise<void> => {
    const url = this.remoteUrl
    if (!url) return

    const apiVersion = this.rootStore.appVer?.apiVersion
    const graphqlApiVersion =
      typeof apiVersion === 'string' && apiVersion.length > 0 ? apiVersion : null
    if (graphqlApiVersion !== null && this.lastPersistedApiVersion === graphqlApiVersion) {
      return
    }

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REMOTE_FETCH_TIMEOUT_MS)
    try {
      const res = await fetch(url, { signal: controller.signal })
      if (!res.ok) return
      const data = await res.json() as unknown
      const terms = parseSearchManifestResponse(data)
      if (!terms) return
      this.setActiveTerms(terms)
      await this.persistToStorage(terms, graphqlApiVersion)
    } catch (error) {
      this.rootStore.errorHandler.captureException(error, {
        context: 'SearchManifestStore.fetchRemote',
        url,
      })
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * Await before handling a deep link that uses `r=` so routing sees fresh remote/cache terms when available.
   */
  loadForDeepLink = async (): Promise<void> => {
    await this.hydrateFromStorage()
    await this.fetchRemote()
  }

  /**
   * Fire-and-forget refresh after normal startup (does not block shell load).
   */
  refreshInBackground = (): void => {
    if (this.backgroundRefreshStarted) return
    this.backgroundRefreshStarted = true
    void (async () => {
      await this.hydrateFromStorage()
      await this.fetchRemote()
    })()
  }

  @action deleteCache = (): void => {
    const { storage } = this.rootStore.storeDataSync
    if (!storage) return
    storage.removeItem(this.storageKey)
    this.lastPersistedApiVersion = null
    this.activeTerms = []
  }
}
